<template>
  <div>
    <!-- co tenants Card -->
    <ValidationObserver ref="tenancyCoTenantsForm" v-slot="{ invalid, validated, changed, validate }">
      <div class="row align-items-center">
        <div class="col">
          <h6 v-if="coTenants">Medleietaker{{ coTenants.length === 1 ? '' : 'e' }}</h6>
        </div>
        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              :loading="loading.saving"
              :disabled="!changed && (!validated || invalid || loading.saving)"
              @click.native="saveAndCloseEdit"
              class="ml-1"
            />
          </div>
          <EditButton v-else @click.native="startEditing(validate)" />
        </div>
      </div>
      <List v-if="coTenants.length || editMode" class="mt-5">
        <ListHeader v-if="coTenants.length">
          <div class="col-5">Navn</div>
          <div class="col coTenant-phone">Telefon</div>
          <div class="col">E-post</div>
          <div v-if="editMode" class="col delete-coTenant"></div>
        </ListHeader>
        <div v-if="editMode">
          <!-- Existing tenants -->
          <ListItem v-for="(coTenant, i) in coTenants" :key="coTenant.id">
            <div class="col-5">
              <ValidationProvider :name="`Navn ${i}`" rules="required" v-slot="{ errors }">
                <hl-Input
                  size="sm"
                  type="text"
                  v-model="coTenant.fullName"
                  :error="errors[0]"
                  @input="updateRowPropertyValue(i, 'text', arguments[0])"
                />
              </ValidationProvider>
            </div>
            <div class="col coTenant-phone">
              <ValidationProvider name="Telefon" rules="required|valid-cell-phone" v-slot="{ errors }">
                <hl-Input
                  size="sm"
                  type="tel"
                  v-model="coTenant.cellPhone"
                  :error="errors[0]"
                  @input="updateRowPropertyValue(i, 'text', arguments[0])"
                />
              </ValidationProvider>
            </div>
            <div class="col">
              <ValidationProvider :name="`E-post ${i}`" rules="required|email" v-slot="{ errors }">
                <hl-Input
                  size="sm"
                  type="email"
                  v-model="coTenant.email"
                  :error="errors[0]"
                  @input="updateRowPropertyValue(i, 'text', arguments[0])"
                />
              </ValidationProvider>
            </div>
            <div class="col delete-coTenant remove-row">
              <button class="btn btn-white py-0">
                <i class="budicon-trash" @click="removeExistingCoTenant(coTenant.id)"></i>
              </button>
            </div>
          </ListItem>
          <!-- Local tenants -->
          <div v-if="localCopy.coTenants.length" class="mt-4">
            <h6>Nye medleietakere</h6>
          </div>
          <ListHeader v-if="localCopy.coTenants.length">
            <div class="col-5">Navn</div>
            <div class="col coTenant-phone">Telefon</div>
            <div class="col">E-post</div>
            <div v-if="editMode" class="col delete-coTenant"></div>
          </ListHeader>
          <ListItem v-for="(coTenant, i) in localCopy.coTenants" :key="i">
            <div class="col-5">
              <ValidationProvider name="Navn" rules="required" v-slot="{ errors }">
                <hl-Input size="sm" type="text" :error="errors[0]" v-model="coTenant.fullName" />
              </ValidationProvider>
            </div>
            <div class="col coTenant-phone">
              <div class="row">
                <div class="col">
                  <ValidationProvider name="Telefon" rules="required|valid-cell-phone" v-slot="{ errors }">
                    <hl-Input size="sm" type="tel" :error="errors[0]" v-model="coTenant.cellPhone" />
                  </ValidationProvider>
                </div>
              </div>
            </div>
            <div class="col">
              <ValidationProvider name="E-post" rules="required|email" v-slot="{ errors }">
                <hl-Input size="sm" type="email" :error="errors[0]" v-model="coTenant.email" />
              </ValidationProvider>
            </div>
            <div class="col delete-coTenant remove-row">
              <i class="budicon-trash" @click="removeNewCoTenant(i)"></i>
            </div>
          </ListItem>
          <ListItem v-if="editMode" class="add-row mb-4 p-0">
            <div class="col text-center">
              <hl-Button variant="secondary" outline class="w-100" @click.native="addCoTenantItem" :disabled="invalid">
                Legg til medleietaker
              </hl-Button>
            </div>
          </ListItem>
        </div>
        <div v-else>
          <ListItem v-for="coTenant in coTenants" :key="coTenant.id">
            <div class="col-5">{{ coTenant.fullName }}</div>
            <div class="col coTenant-phone">{{ coTenant.cellPhone }}</div>
            <div class="col">{{ coTenant.email }}</div>
          </ListItem>
        </div>
      </List>
      <!-- No coTenants -->
      <div v-else class="mt-5 text-center">
        <img src="@/assets/empty-interest-management.svg" width="140" />
        <h5 class="mt-5">Leieforholdet har ingen medleietakere</h5>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import List from '@/components/UI/list/List';
import ListHeader from '@/components/UI/list/ListHeader';
import ListItem from '@/components/UI/list/ListItem';
import SaveButton from '@/components/UI/buttons/SaveButton';
import { mapGetters, mapActions } from 'vuex';
import { validate } from 'vee-validate';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    List,
    ListHeader,
    ListItem,
    SaveButton,
    'hl-Input': Input,
    'hl-Button': Button,
  },

  props: ['landlordId', 'tenancyId'],

  data() {
    return {
      editMode: false,
      coTenants: [],
      existingCoTenantsToBeRemoved: [],
      loading: {
        saving: false,
      },
      localCopy: {
        coTenants: [],
      },
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancy']),

    dirtyCoTenants() {
      return this.coTenants.filter(coTenant => coTenant.isDirty);
    },

    noEmptyFieldsOnNewTenants() {
      for (const coTenant of this.localCopy.coTenants) {
        if (coTenant.fullName.length === 0 || coTenant.cellPhone.length === 0 || coTenant.email.length === 0)
          return false;
      }
      return true;
    },
  },

  methods: {
    ...mapActions('tenancy', ['addTenancyTenant', 'updateTenancyTenant', 'fetchTenancyTenants', 'deleteTenancyTenant']),

    startEditing(callBack) {
      this.existingCoTenantsToBeRemoved = [];
      this.localCopy = {
        coTenants: [],
      };
      this.editMode = true;
      console.log('validate', validate());
      callBack();
    },

    updateRowPropertyValue(rowIndex, prop, val) {
      this.coTenants.splice(rowIndex, 1, {
        ...this.coTenants[rowIndex],
        [prop]: val,
      });
      this.coTenants[rowIndex].isDirty = true;
    },

    addCoTenantItem() {
      this.localCopy.coTenants.push({
        fullName: '',
        cellPhone: '',
        email: '',
        type: 'cotenant',
      });
      this.$nextTick(() => {
        this.$refs.tenancyCoTenantsForm.validate();
      });
    },

    removeNewCoTenant(i) {
      this.localCopy.coTenants.splice(i, 1);
    },

    removeExistingCoTenant(id) {
      const index = this.coTenants.findIndex(coTenant => {
        return coTenant.id === id;
      });

      this.coTenants.splice(index, 1);
      this.existingCoTenantsToBeRemoved.push(id);
      this.$nextTick(() => {
        this.$refs.tenancyCoTenantsForm.validate();
      });
    },

    async saveAndCloseEdit() {
      const [landlordId, tenancyId] = [this.landlordId, this.tenancyId];
      // api
      try {
        this.loading.saving = true;
        await Promise.all([
          this.updateExisitingTenants(landlordId, tenancyId),
          this.addNewCoTenants(landlordId, tenancyId),
          this.deleteCoTenants(landlordId, tenancyId),
        ]);
        this.editMode = false;
        this.setCoTenants();
        this.$toast('Lagret', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre', 'error');
      }
      this.loading.saving = false;
    },

    async updateExisitingTenants(landlordId, tenancyId) {
      try {
        const existingCoTenants = this.dirtyCoTenants;

        for (const coTenant of existingCoTenants) {
          await this.updateTenancyTenant({
            landlordId,
            tenancyId,
            tenantId: coTenant.id,
            data: coTenant,
          });
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre leietaker.', 'error');
      }
    },

    async addNewCoTenants(landlordId, tenancyId) {
      try {
        const newCoTenants = this.localCopy.coTenants;
        for (const coTenant of newCoTenants) {
          await this.addTenancyTenant({
            landlordId,
            tenancyId,
            data: coTenant,
          });
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre medleietaker.', 'error');
      }
    },

    async deleteCoTenants(landlordId, tenancyId) {
      try {
        for (const coTenant of this.existingCoTenantsToBeRemoved) {
          await this.deleteTenancyTenant({
            landlordId,
            tenancyId,
            tenantId: coTenant,
          });
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette leietaker.', 'error');
      }
    },

    async setCoTenants() {
      try {
        const tenants = await this.fetchTenancyTenants({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
        if (tenants == null) return;
        const coTenants = tenants.filter(tenant => tenant.type === 'cotenant');
        this.coTenants = JSON.parse(JSON.stringify(coTenants));
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente leietakere.', 'error');
      }
    },
  },

  mounted() {
    this.setCoTenants();
  },
};
</script>

<style scoped lang="scss">
.delete-coTenant {
  flex: 0 0 40px;
  text-align: right;
}

.coTenant-phone {
  flex: 0 0 180px;
}

.add-row {
  background: $gray-100;
  color: $gray-800;
  box-shadow: $box-shadow-1;
  cursor: pointer;
}

.remove-row {
  i {
    font-size: 1rem;
  }
}

.edit-buttons {
  flex: 0 0 200px;
}
</style>
